import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  APIcreateFileSize,
  APIupdateFileSize,
} from '../../../api/Files';

import { addFlashMessage } from '../../../helpers/FlashMessages';

import ProcessingSpinner from '../../ProcessingSpinner';

import ErrorBoundary from '../../ErrorBoundary';
import HelpIcon from '../../HelpIcon';

class FileSizeEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      name: null,
      width: null,
      height: null,
      crop: false,
      errors: [],
      saving: false,
    };

    this.inputRef = React.createRef();

    this.handleChange = this.handleChange.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
  }

  componentDidMount() {
    this.setInitialState();
  }

  setInitialState() {
    const { size } = this.props;

    if (size) {
      this.setState({
        name: size.name,
        width: size.width,
        height: size.height,
        crop: size.crop,
      }, () => {
        this.inputRef.selectionStart = this.inputRef.value.length;
        this.inputRef.selectionEnd = this.inputRef.value.length;
      });
    }
  }

  isCropDisabled() {
    const {
      width,
      height,
    } = this.state;

    let w = true;
    let h = true;
    if (!width || parseInt(width) === 0) {
      w = false;
    }
    if (!height || parseInt(height) === 0) {
      h = false;
    }

    return !(w && h);
  }

  handleUpdate() {
    const {
      size,
      afterCreateSize,
      handleClose,
    } = this.props;
    const {
      name,
      width,
      height,
      crop,
    } = this.state;

    const errors = [];
    if (!name) {
      errors.push('name');
    }
    this.setState({ errors });
    if (errors.length > 0) {
      return;
    }

    this.setState({
      saving: true,
      errors: [],
    });

    if (size) {
      size.name = name;
      size.width = width;
      size.height = height;
      size.crop = crop;
      APIupdateFileSize(size).then((response) => {
        const { data } = response;
        if (data) {
          afterCreateSize(data);
        }
        addFlashMessage({
          type: 'success',
          text: 'Your changes have been saved',
        });
        handleClose();
      });
    } else {
      APIcreateFileSize({
        name,
        width,
        height,
        crop,
      }).then((response) => {
        const { data } = response;
        if (data) {
          afterCreateSize(data);
        }
        addFlashMessage({
          type: 'success',
          text: 'New file size has been created',
        });
        handleClose();
      });
    }
  }

  handleChange(e) {
    const {
      name,
      value,
      checked,
    } = e.target;

    if (name === 'crop') {
      this.setState({ crop: checked });
    } else {
      const { state } = this;
      state[name] = value;
      this.setState(state, () => {
        if (this.isCropDisabled()) {
          this.setState({ crop: false });
        }
      });
    }
  }

  render() {
    const {
      intl,
      size,
      handleClose,
    } = this.props;
    const {
      name,
      width,
      height,
      crop,
      errors,
      saving,
    } = this.state;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {size ? intl.formatMessage({ defaultMessage: 'Edit file size' }) : intl.formatMessage({ defaultMessage: 'New file size' })}
        </ModalHeader>
        <ModalBody>
          <Form className="edit-size-form" onSubmit={(e) => { e.preventDefault(); this.handleUpdate(); }}>
            <FormGroup>
              <Label for="sizeName">
                {intl.formatMessage({ defaultMessage: 'Size name' })}
              </Label>
              <Input
                type="text"
                id="sizeName"
                name="name"
                autoFocus={true}
                innerRef={(ref) => { this.inputRef = ref; }}
                disabled={saving}
                defaultValue={name}
                onChange={this.handleChange}
                invalid={errors.includes('name')}
              />
              {errors.includes('name') && (
                <FormFeedback>{intl.formatMessage({ defaultMessage: 'This field is required' })}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup row>
              <Col sm={6}>
                <Label for="sizeWidth">
                  {intl.formatMessage({ defaultMessage: 'Width' })}
                </Label>
                <Input
                  type="number"
                  id="sizeWidth"
                  name="width"
                  min={0}
                  disabled={saving}
                  defaultValue={width}
                  onChange={this.handleChange}
                />
              </Col>
              <Col sm={6}>
                <Label for="sizeHeight">
                  {intl.formatMessage({ defaultMessage: 'Height' })}
                </Label>
                <Input
                  type="number"
                  id="sizeHeight"
                  name="height"
                  min={0}
                  disabled={saving}
                  defaultValue={height}
                  onChange={this.handleChange}
                />
              </Col>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="crop"
                  className="input-check check-s form-check-input no-margin"
                  value={true}
                  checked={crop}
                  onChange={this.handleChange}
                  disabled={this.isCropDisabled()}
                />
                {' '}
                {intl.formatMessage({ defaultMessage: 'Crop images to the exact dimensions' })}
                <HelpIcon>
                  {intl.formatMessage({ defaultMessage: 'Specify the height and width to crop images. Selecting this checkbox will crop images to your specified dimensions.' })}
                </HelpIcon>
              </Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving}
              onClick={this.handleUpdate}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

FileSizeEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  size: PropTypes.object,
  afterCreateSize: PropTypes.func,
};

FileSizeEditModal.defaultProps = {
  size: null,
  afterCreateSize: () => {},
};

export default injectIntl(FileSizeEditModal);
